import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Badge,
  Box,
  BlockStack,
  InlineStack,
  Grid,
  Spinner,
  IndexTable,
  IndexFilters,
  useIndexResourceState,
  useSetIndexFiltersMode,
  SkeletonBodyText,
  SkeletonDisplayText,
  Button,
  ButtonGroup,
  Pagination,
  ProgressBar,
  Tooltip,
  Icon,
  ChoiceList,
  EmptyState,
  Banner,
  TextField,
  Select,
  Checkbox,
  FormLayout,
  Modal
} from '@shopify/polaris';
import { AlertTriangleIcon, RefreshIcon, PlusIcon, SettingsIcon } from '@shopify/polaris-icons';

import { InventoryAnalyticsReport } from '../../types/inventory-analytics';

const SimpleInventoryAnalytics: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<InventoryAnalyticsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStage, setLoadingStage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  
  // IndexFilters state
  const [queryValue, setQueryValue] = useState('');
  const [sortSelected, setSortSelected] = useState(['stock_status asc']);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTab, setSelectedTab] = useState(0);
  const { mode, setMode } = useSetIndexFiltersMode();
  
  const itemsPerPage = 25;

  // Tab configuration
  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
  const [tabStrings, setTabStrings] = useState([
    'All Items',
    'Critical & Urgent',
    'Low Stock',
    'Overstock'
  ]);

  // Advanced filtering state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [topSellingOnly, setTopSellingOnly] = useState(false);
  const [topSellingCount, setTopSellingCount] = useState('100');
  const [rankingMethod, setRankingMethod] = useState('volume');
  const [minSalesThreshold, setMinSalesThreshold] = useState('5');

  const rankingOptions = [
    { label: 'Sales Volume (Quantity)', value: 'volume' },
    { label: 'Sales Revenue (Value)', value: 'revenue' },
  ];

  const deleteView = (index: number) => {
    const newTabStrings = [...tabStrings];
    newTabStrings.splice(index, 1);
    setTabStrings(newTabStrings);
    setSelectedTab(0);
  };

  const duplicateView = async (name: string) => {
    setTabStrings([...tabStrings, name]);
    setSelectedTab(tabStrings.length);
    await sleep(1);
    return true;
  };

  const onCreateNewView = async (value: string) => {
    await sleep(500);
    setTabStrings([...tabStrings, value]);
    setSelectedTab(tabStrings.length);
    return true;
  };

  const tabs = tabStrings.map((item, index) => ({
    content: item,
    index,
    onAction: () => {},
    id: `${item}-${index}`,
    isLocked: index === 0,
    actions: index === 0 ? [] : [
      {
        type: 'rename' as const,
        onAction: () => {},
        onPrimaryAction: async (value: string): Promise<boolean> => {
          const newTabStrings = tabs.map((tab, idx) => {
            if (idx === index) {
              return value;
            }
            return tab.content;
          });
          await sleep(1);
          setTabStrings(newTabStrings);
          return true;
        },
      },
      {
        type: 'duplicate' as const,
        onPrimaryAction: async (name: string): Promise<boolean> => {
          await sleep(1);
          duplicateView(name);
          return true;
        },
      },
      {
        type: 'edit' as const,
      },
      {
        type: 'delete' as const,
        onPrimaryAction: async (): Promise<boolean> => {
          await sleep(1);
          deleteView(index);
          return true;
        },
      },
    ],
  }));

  // Sort options
  const sortOptions = [
    { label: 'Stock Status', value: 'stock_status asc' as const, directionLabel: 'Critical first' },
    { label: 'Stock Status', value: 'stock_status desc' as const, directionLabel: 'OK first' },
    { label: 'Days of Inventory', value: 'days_of_inventory asc' as const, directionLabel: 'Low to high' },
    { label: 'Days of Inventory', value: 'days_of_inventory desc' as const, directionLabel: 'High to low' },
    { label: 'Current Stock', value: 'current_stock asc' as const, directionLabel: 'Low to high' },
    { label: 'Current Stock', value: 'current_stock desc' as const, directionLabel: 'High to low' },
    { label: 'Value in Stock', value: 'value_in_stock asc' as const, directionLabel: 'Low to high' },
    { label: 'Value in Stock', value: 'value_in_stock desc' as const, directionLabel: 'High to low' },
    { label: 'SKU', value: 'sku asc' as const, directionLabel: 'A-Z' },
    { label: 'SKU', value: 'sku desc' as const, directionLabel: 'Z-A' },
  ];

  // Helper functions
  const isEmpty = (value: any) => {
    if (Array.isArray(value)) {
      return value.length === 0;
    } else {
      return value === '' || value == null;
    }
  };

  const disambiguateLabel = (key: string, value: string[]): string => {
    switch (key) {
      case 'status':
        return value.map((val) => `Status: ${val}`).join(', ');
      case 'type':
        return value.map((val) => `Type: ${val}`).join(', ');
      default:
        return value.join(', ');
    }
  };

  // Filter management
  const handleStatusChange = useCallback((value: string[]) => setStatusFilter(value), []);
  const handleTypeChange = useCallback((value: string[]) => setTypeFilter(value), []);
  const handleFiltersQueryChange = useCallback((value: string) => setQueryValue(value), []);
  const handleQueryValueRemove = useCallback(() => setQueryValue(''), []);
  const handleStatusRemove = useCallback(() => setStatusFilter([]), []);
  const handleTypeRemove = useCallback(() => setTypeFilter([]), []);
  const handleFiltersClearAll = useCallback(() => {
    handleStatusRemove();
    handleTypeRemove();
    handleQueryValueRemove();
  }, [handleStatusRemove, handleQueryValueRemove, handleTypeRemove]);

  const onHandleCancel = () => {
    // Reset filters to saved state
  };

  const onHandleSave = async () => {
    // Save current filter state
    await sleep(1);
    return true;
  };

  const primaryAction = selectedTab === 0 ? {
    type: 'save-as' as const,
    onAction: onCreateNewView,
    disabled: false,
    loading: false,
  } : {
    type: 'save' as const,
    onAction: onHandleSave,
    disabled: false,
    loading: false,
  };

  // Filters configuration
  const filters = [
    {
      key: 'status',
      label: 'Stock Status',
      filter: (
        <ChoiceList
          title="Stock Status"
          titleHidden
          choices={[
            { label: 'Critical', value: 'critical' },
            { label: 'Urgent', value: 'urgent' },
            { label: 'Soon', value: 'soon' },
            { label: 'OK', value: 'ok' },
            { label: 'Overstock', value: 'overstock' },
            { label: 'Dead Stock', value: 'deadstock' }
          ]}
          selected={statusFilter}
          onChange={handleStatusChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'type',
      label: 'Item Type',
      filter: (
        <ChoiceList
          title="Item Type"
          titleHidden
          choices={[
            { label: 'Product', value: 'product' },
            { label: 'Material', value: 'material' }
          ]}
          selected={typeFilter}
          onChange={handleTypeChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
  ];

  // Applied filters
  const appliedFilters = [];
  if (statusFilter && !isEmpty(statusFilter)) {
    appliedFilters.push({
      key: 'status',
      label: disambiguateLabel('status', statusFilter),
      onRemove: handleStatusRemove,
    });
  }
  if (typeFilter && !isEmpty(typeFilter)) {
    appliedFilters.push({
      key: 'type',
      label: disambiguateLabel('type', typeFilter),
      onRemove: handleTypeRemove,
    });
  }

  // Set up IndexTable resource state
  const resourceName = {
    singular: 'inventory item',
    plural: 'inventory items',
  };

  const {
    selectedResources,
    allResourcesSelected,
    handleSelectionChange,
  } = useIndexResourceState((analyticsData?.analytics || []) as any);


  // Data loading function
  const fetchAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setLoadingProgress(0);
      setLoadingStage('Initializing...');
      
      const stages = [
        'Fetching inventory data (page 1)...',
        'Loading additional pages...',
        'Processing product variants...',
        'Calculating velocity analytics...',
        'Processing stock status...',
        'Generating recommendations...',
        'Finalizing report...'
      ];
      
      let currentStage = 0;
      const progressInterval = setInterval(() => {
        if (currentStage < stages.length) {
          setLoadingStage(stages[currentStage]);
          setLoadingProgress(((currentStage + 1) / stages.length) * 90);
          currentStage++;
        }
      }, 2000);
      
      // Build query parameters for the analytics endpoint
      const params = new URLSearchParams();
      
      // Add required shop parameter
      const urlParams = new URLSearchParams(window.location.search);
      const shop = urlParams.get('shop');
      if (shop) {
        params.append('shop', shop);
      }
      
      // Add analytics-specific parameters
      params.append('include_suggestions', 'true');
      params.append('include_alerts', 'true');
      params.append('include_manufacturing', 'true');
      
      // Add advanced filtering parameters if enabled
      if (topSellingOnly) {
        params.append('top_selling_only', 'true');
        params.append('top_selling_count', topSellingCount);
        params.append('ranking_method', rankingMethod);
        params.append('min_sales_threshold', minSalesThreshold);
      }
      
      console.log('🚀 Fetching comprehensive inventory analytics...');
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`/api/katana/inventory-analytics${queryString}`);
      
      if (!response.ok) {
        // Try to get error details from response
        const errorText = await response.text();
        console.error('❌ Analytics API Error Response:', errorText);
        throw new Error(`Failed to fetch analytics: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      clearInterval(progressInterval);
      setLoadingProgress(100);
      setLoadingStage('Complete!');
      
      setTimeout(() => {
        setAnalyticsData(result as InventoryAnalyticsReport);
        setLoading(false);
      }, 300);
      
    } catch (err) {
      setLoading(false);
      setLoadingProgress(0);
      const error = err as Error;
      
      if (error.message.includes('NO_KATANA_CONFIG')) {
        setError('Katana API not configured. Please check your API settings.');
      } else if (error.message.includes('rate limit')) {
        setError('API rate limit exceeded. Please wait a moment and try again.');
      } else if (error.message.includes('timeout')) {
        setError('Request timed out. Please check your connection and try again.');
      } else {
        setError(`Failed to load analytics: ${error.message}`);
      }
      
      console.error('Analytics fetch error:', err);
    }
  }, [topSellingOnly, topSellingCount, rankingMethod, minSalesThreshold]);

  // Auto-refresh functionality
  useEffect(() => {
    let interval: number;
    if (autoRefresh && !loading) {
      interval = setInterval(() => {
        fetchAnalyticsData();
      }, 600000); // 10 minutes (longer due to pagination)
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, loading, fetchAnalyticsData]);

  // Filter and sort analytics data
  const filteredAndSortedData = useMemo(() => {
    if (!analyticsData?.analytics) return [];
    
    let filtered = analyticsData.analytics.filter(item => {
      // Tab-based filtering
      if (selectedTab === 1) { // Critical & Urgent
        if (!['critical', 'urgent'].includes(item.stock_status)) return false;
      } else if (selectedTab === 2) { // Low Stock
        if (!['critical', 'urgent', 'soon'].includes(item.stock_status)) return false;
      } else if (selectedTab === 3) { // Overstock
        if (!['overstock', 'deadstock'].includes(item.stock_status)) return false;
      }
      
      // Search filter
      const matchesSearch = !queryValue || 
        item.sku.toLowerCase().includes(queryValue.toLowerCase()) ||
        (item.variant_name && item.variant_name.toLowerCase().includes(queryValue.toLowerCase()));
      
      // Status filter
      const matchesStatus = statusFilter.length === 0 || 
        statusFilter.includes(item.stock_status);
      
      // Type filter
      const matchesType = typeFilter.length === 0 || 
        typeFilter.includes(item.bom_level === 0 ? 'product' : 'material');
      
      return matchesSearch && matchesStatus && matchesType;
    });
    
    // Sort data
    const [sortKey, sortDirection] = sortSelected[0].split(' ');
    const isAscending = sortDirection === 'asc';
    
    filtered.sort((a, b) => {
      switch (sortKey) {
        case 'stock_status':
          const statusOrder = { critical: 0, urgent: 1, soon: 2, ok: 3, overstock: 4, deadstock: 5 };
          const aValue = statusOrder[a.stock_status as keyof typeof statusOrder] ?? 6;
          const bValue = statusOrder[b.stock_status as keyof typeof statusOrder] ?? 6;
          return isAscending ? aValue - bValue : bValue - aValue;
        
        case 'days_of_inventory':
          const aDays = a.days_of_inventory ?? 999;
          const bDays = b.days_of_inventory ?? 999;
          return isAscending ? aDays - bDays : bDays - aDays;
        
        case 'current_stock':
          const aStock = a.current_stock ?? 0;
          const bStock = b.current_stock ?? 0;
          return isAscending ? aStock - bStock : bStock - aStock;
        
        case 'value_in_stock':
          const aValue2 = a.value_in_stock ?? 0;
          const bValue2 = b.value_in_stock ?? 0;
          return isAscending ? aValue2 - bValue2 : bValue2 - aValue2;
        
        case 'sku':
          const aSku = a.sku.toLowerCase();
          const bSku = b.sku.toLowerCase();
          return isAscending ? aSku.localeCompare(bSku) : bSku.localeCompare(aSku);
        
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [analyticsData, queryValue, statusFilter, typeFilter, sortSelected, selectedTab]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedData, currentPage, itemsPerPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [queryValue, statusFilter, typeFilter, selectedTab]);

  const formatCurrency = (amount: number | null | undefined) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      critical: 'critical' as const,
      urgent: 'warning' as const,
      soon: 'info' as const,
      ok: 'success' as const,
      overstock: 'info' as const,
      deadstock: 'info' as const
    };
    
    return (
      <Badge tone={statusConfig[status as keyof typeof statusConfig] || 'info'}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  // Bulk actions
  const promotedBulkActions = [
    {
      content: 'Create Purchase Orders',
      onAction: () => {
        console.log('Create POs for selected items:', selectedResources);
        // TODO: Implement bulk PO creation
      },
    },
    {
      content: 'Export Selected',
      onAction: () => {
        console.log('Export selected items:', selectedResources);
        // TODO: Implement export functionality
      },
    },
  ];

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  const handleRefresh = () => {
    fetchAnalyticsData();
  };

  const handleAdvancedFiltersChange = () => {
    // Trigger refetch when filters are applied
    fetchAnalyticsData();
    setShowAdvancedFilters(false);
  };

  if (loading) {
    return (
      <Page title="Inventory Analytics">
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="600">
                <BlockStack gap="400" align="center">
                  <SkeletonDisplayText size="medium" />
                  <Box width="100%" maxWidth="400px">
                    <ProgressBar progress={loadingProgress} size="small" />
                  </Box>
                  <InlineStack align="center" gap="200">
                    <Spinner size="small" />
                    <Text as="p" variant="bodyMd" tone="subdued">{loadingStage}</Text>
                  </InlineStack>
                  <Text as="p" variant="bodySm" tone="subdued">
                    This may take up to 2 minutes for large inventories with multiple pages
                  </Text>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
          
          <Layout.Section>
            <Grid columns={{ xs: 1, sm: 2, md: 2, lg: 4 }}>
              {[1, 2, 3, 4].map(i => (
                <Card key={i}>
                  <Box padding="400">
                    <BlockStack gap="200">
                      <SkeletonBodyText lines={1} />
                      <SkeletonDisplayText size="small" />
                    </BlockStack>
                  </Box>
                </Card>
              ))}
            </Grid>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Inventory Analytics">
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="600">
                <BlockStack gap="400" align="center">
                  <Icon source={AlertTriangleIcon} tone="critical" />
                  <Text as="h2" variant="headingLg" alignment="center">Analytics Unavailable</Text>
                  <Text as="p" variant="bodyMd" alignment="center" tone="subdued">
                    {error}
                  </Text>
                  <ButtonGroup>
                    <Button onClick={handleRefresh} variant="primary">
                      Try Again
                    </Button>
                    <Button 
                      url="/settings" 
                      variant="secondary"
                      disabled={!error.includes('API not configured')}
                    >
                      Check Settings
                    </Button>
                  </ButtonGroup>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Need help? Check that your Katana API is properly configured.
                  </Text>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!analyticsData) {
    return (
      <Page title="Inventory Analytics">
        <Layout>
          <Layout.Section>
            <Card>
              <EmptyState
                heading="No analytics data available"
                action={{
                  content: 'Load Analytics Data',
                  onAction: fetchAnalyticsData,
                }}
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>Generate your inventory analytics report to view insights and recommendations.</p>
              </EmptyState>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const advancedFiltersModal = (
    <Modal
      open={showAdvancedFilters}
      onClose={() => setShowAdvancedFilters(false)}
      title="Advanced Inventory Analysis"
      primaryAction={{
        content: 'Apply Filters',
        onAction: handleAdvancedFiltersChange,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: () => setShowAdvancedFilters(false),
        },
      ]}
    >
      <Modal.Section>
        <FormLayout>
          <Checkbox
            label="Focus on top-selling products only"
            helpText="Analyze only your best-performing products to prevent backorders on high-impact items"
            checked={topSellingOnly}
            onChange={setTopSellingOnly}
          />
          
          {topSellingOnly && (
            <>
              <TextField
                label="Number of top products to analyze"
                type="number"
                value={topSellingCount}
                onChange={setTopSellingCount}
                helpText="Maximum number of top-selling products to include in analysis"
                min="10"
                max="500"
                autoComplete="off"
              />
              
              <Select
                label="Ranking method"
                options={rankingOptions}
                value={rankingMethod}
                onChange={setRankingMethod}
                helpText="Choose how to rank products: by quantity sold or revenue generated"
              />
              
              <TextField
                label="Minimum sales threshold"
                type="number"
                value={minSalesThreshold}
                onChange={setMinSalesThreshold}
                helpText="Minimum units sold in last 180 days to be considered for analysis"
                min="1"
                max="100"
                autoComplete="off"
              />
            </>
          )}
        </FormLayout>
      </Modal.Section>
    </Modal>
  );

  return (
    <Page 
      title="Inventory Analytics"
      titleMetadata={analyticsData.summary && 'analysis_scope' in analyticsData.summary && (analyticsData.summary as any).analysis_scope === 'top_selling' ? (
        <Badge tone="info">Top-selling focus</Badge>
      ) : undefined}
      primaryAction={{
        content: 'Refresh',
        icon: RefreshIcon,
        onAction: handleRefresh,
        loading: loading,
      }}
      secondaryActions={[
        {
          content: autoRefresh ? 'Stop Auto-refresh' : 'Auto-refresh (10m)',
          onAction: () => setAutoRefresh(!autoRefresh),
          icon: RefreshIcon
        },
        {
          content: 'Advanced Filters',
          icon: SettingsIcon,
          onAction: () => setShowAdvancedFilters(true),
        },
      ]}
    >
      {advancedFiltersModal}
      
      <Layout>
        {/* AI Insights Banner */}
        {analyticsData.ai_insights && analyticsData.ai_insights.length > 0 && (
          <Layout.Section>
            <Banner
              title="AI Insights Available"
              tone="info"
              action={{
                content: `View ${analyticsData.ai_insights.length} Insights`,
                onAction: () => console.log('View AI insights')
              }}
            >
              <p>
                AI analysis detected {analyticsData.ai_insights.filter(i => i.urgency === 'immediate').length} immediate actions and 
                {analyticsData.ai_insights.filter(i => i.type === 'opportunity').length} optimization opportunities.
              </p>
            </Banner>
          </Layout.Section>
        )}

        {/* Analysis Scope Banner */}
        {analyticsData.summary && 'top_selling_config' in analyticsData.summary && (analyticsData.summary as any).top_selling_config && (
          <Box paddingBlockEnd="400">
            <Banner tone="info" title="Top-Selling Analysis Active">
              <p>
                Analyzing top {(analyticsData.summary as any).top_selling_config.count} products by{' '}
                {(analyticsData.summary as any).top_selling_config.ranking_method} over{' '}
                {(analyticsData.summary as any).top_selling_config.analysis_period_days} days.{' '}
                {(analyticsData.summary as any).top_selling_config.variants_filtered_out} variants filtered out.
              </p>
            </Banner>
          </Box>
        )}

        {/* Enhanced Summary Cards */}
        <Layout.Section>
          <Grid columns={{ xs: 1, sm: 2, md: 2, lg: 6 }}>
            <Card>
              <Box padding="400" minHeight="120px">
                <BlockStack gap="200" align="space-between">
                  <Text as="h3" variant="headingSm" tone="subdued">Total Variants</Text>
                  <Text as="p" variant="heading2xl">{analyticsData.summary.total_variants}</Text>
                </BlockStack>
              </Box>
            </Card>
            
            <Card>
              <Box padding="400" minHeight="120px">
                <BlockStack gap="200" align="space-between">
                  <Text as="h3" variant="headingSm" tone="subdued">Critical Items</Text>
                  <Text as="p" variant="heading2xl" tone="critical">
                    {analyticsData.summary.by_stock_status.critical || 0}
                  </Text>
                </BlockStack>
              </Box>
            </Card>
            
            <Card>
              <Box padding="400" minHeight="120px">
                <BlockStack gap="200" align="space-between">
                  <Text as="h3" variant="headingSm" tone="subdued">Urgent Items</Text>
                  <Text as="p" variant="heading2xl" tone="critical">
                    {analyticsData.summary.by_stock_status.urgent || 0}
                  </Text>
                </BlockStack>
              </Box>
            </Card>
            
            <Card>
              <Box padding="400" minHeight="120px">
                <BlockStack gap="200" align="space-between">
                  <Text as="h3" variant="headingSm" tone="subdued">Value at Risk</Text>
                  <Text as="p" variant="heading2xl" tone="critical">
                    {formatCurrency(analyticsData.summary.total_value_at_risk || 0)}
                  </Text>
                </BlockStack>
              </Box>
            </Card>
            
            <Card>
              <Box padding="400" minHeight="120px">
                <BlockStack gap="200" align="space-between">
                  <Text as="h3" variant="headingSm" tone="subdued">Service Level</Text>
                  <Text as="p" variant="heading2xl" tone="success">
                    {analyticsData.summary.average_service_level ? 
                      `${(analyticsData.summary.average_service_level * 100).toFixed(1)}%` : 
                      '95.0%'}
                  </Text>
                </BlockStack>
              </Box>
            </Card>
            
            <Card>
              <Box padding="400" minHeight="120px">
                <BlockStack gap="200" align="space-between">
                  <Text as="h3" variant="headingSm" tone="subdued">AI Confidence</Text>
                  <Text as="p" variant="heading2xl">
                    {analyticsData.metadata.confidence_level === 'high' ? '95%' : 
                     analyticsData.metadata.confidence_level === 'medium' ? '75%' : '60%'}
                  </Text>
                </BlockStack>
              </Box>
            </Card>
          </Grid>
        </Layout.Section>

        {/* AI Insights Section */}
        {analyticsData.ai_insights && analyticsData.ai_insights.length > 0 && (
          <Layout.Section>
            <Card>
              <Box padding="600">
                <BlockStack gap="400">
                  <Text as="h2" variant="headingLg">AI-Powered Insights</Text>
                  <BlockStack gap="400">
                    {analyticsData.ai_insights.slice(0, 3).map((insight) => (
                      <Card key={insight.id} background="bg-surface-secondary">
                        <Box padding="400">
                          <BlockStack gap="300">
                            <InlineStack align="space-between">
                              <Text as="h3" variant="headingMd">{insight.title}</Text>
                              <Badge tone={insight.urgency === 'immediate' ? 'critical' : 'info'}>
                                {insight.urgency.replace('_', ' ').toUpperCase()}
                              </Badge>
                            </InlineStack>
                            <Text as="p" variant="bodyMd">{insight.description}</Text>
                            {insight.financial_impact && (
                              <Badge>{`Potential Value: ${formatCurrency(insight.financial_impact)}`}</Badge>
                            )}
                          </BlockStack>
                        </Box>
                      </Card>
                    ))}
                    {analyticsData.ai_insights.length > 3 && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        +{analyticsData.ai_insights.length - 3} more insights available
                      </Text>
                    )}
                  </BlockStack>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        )}

        {/* Enhanced Performance Metrics */}
        <Layout.Section>
          <Card>
            <Box padding="500">
              <BlockStack gap="500">
                <Text as="h2" variant="headingLg">Performance Intelligence</Text>
                <Grid columns={{ xs: 1, sm: 2, md: 3, lg: 6 }}>
                  <Box padding="300">
                    <BlockStack gap="200">
                      <Text as="p" variant="bodySm" tone="subdued">Inventory Turnover</Text>
                      <Text as="p" variant="headingMd">
                        {(analyticsData.performance_metrics.inventory_turnover || 0).toFixed(2)}x
                      </Text>
                    </BlockStack>
                  </Box>
                  <Box padding="300">
                    <BlockStack gap="200">
                      <Text as="p" variant="bodySm" tone="subdued">Stock-out Risk Score</Text>
                      <Text as="p" variant="headingMd" tone={
                        (analyticsData.performance_metrics.stockout_risk_score || 0) > 20 ? 'critical' : 'success'
                      }>
                        {analyticsData.performance_metrics.stockout_risk_score || 0}%
                      </Text>
                    </BlockStack>
                  </Box>
                  <Box padding="300">
                    <BlockStack gap="200">
                      <Text as="p" variant="bodySm" tone="subdued">Fill Rate</Text>
                      <Text as="p" variant="headingMd" tone="success">
                        {analyticsData.performance_metrics.fill_rate ? 
                          `${(analyticsData.performance_metrics.fill_rate * 100).toFixed(1)}%` : 
                          '98.0%'}
                      </Text>
                    </BlockStack>
                  </Box>
                  <Box padding="300">
                    <BlockStack gap="200">
                      <Text as="p" variant="bodySm" tone="subdued">Forecast Accuracy</Text>
                      <Text as="p" variant="headingMd">
                        {analyticsData.performance_metrics.forecast_accuracy ? 
                          `${(analyticsData.performance_metrics.forecast_accuracy * 100).toFixed(1)}%` : 
                          '85.0%'}
                      </Text>
                    </BlockStack>
                  </Box>
                  <Box padding="300">
                    <BlockStack gap="200">
                      <Text as="p" variant="bodySm" tone="subdued">Dead Stock</Text>
                      <Text as="p" variant="headingMd">
                        {analyticsData.performance_metrics.dead_stock_percentage ? 
                          `${(analyticsData.performance_metrics.dead_stock_percentage * 100).toFixed(1)}%` : 
                          '2.1%'}
                      </Text>
                    </BlockStack>
                  </Box>
                  <Box padding="300">
                    <BlockStack gap="200">
                      <Text as="p" variant="bodySm" tone="subdued">Carrying Cost Ratio</Text>
                      <Text as="p" variant="headingMd">
                        {analyticsData.performance_metrics.carrying_cost_ratio ? 
                          `${(analyticsData.performance_metrics.carrying_cost_ratio * 100).toFixed(1)}%` : 
                          '25.0%'}
                      </Text>
                    </BlockStack>
                  </Box>
                </Grid>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Resource Index Table */}
        <Layout.Section>
          <Card padding="0">
            <IndexFilters
              sortOptions={sortOptions}
              sortSelected={sortSelected}
              queryValue={queryValue}
              queryPlaceholder="Search by SKU or product name..."
              onQueryChange={handleFiltersQueryChange}
              onQueryClear={handleQueryValueRemove}
              onSort={setSortSelected}
              primaryAction={primaryAction}
              cancelAction={{
                onAction: onHandleCancel,
                disabled: false,
                loading: false,
              }}
              tabs={tabs}
              selected={selectedTab}
              onSelect={setSelectedTab}
              canCreateNewView
              onCreateNewView={onCreateNewView}
              filters={filters}
              appliedFilters={appliedFilters}
              onClearAll={handleFiltersClearAll}
              mode={mode}
              setMode={setMode}
            />
            
            {filteredAndSortedData.length === 0 ? (
              <EmptyState
                heading="No inventory items found"
                action={{
                  content: 'Clear all filters',
                  onAction: handleFiltersClearAll,
                }}
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>Try adjusting your search or filter settings.</p>
              </EmptyState>
            ) : (
              <IndexTable
                resourceName={resourceName}
                itemCount={filteredAndSortedData.length}
                selectedItemsCount={
                  allResourcesSelected ? 'All' : selectedResources.length
                }
                onSelectionChange={handleSelectionChange}
                promotedBulkActions={promotedBulkActions}
                sortable={[true, true, false, true, false, true, false, true]}
                headings={[
                  { title: 'Product/Material' },
                  { title: 'SKU' },
                  { title: 'Type' },
                  { title: 'Current Stock', alignment: 'end' },
                  { title: 'Status' },
                  { title: 'Days Left', alignment: 'end' },
                  { title: 'Suggested Order', alignment: 'end' },
                  { title: 'Value', alignment: 'end' },
                ]}
                selectable
              >
                {paginatedData.map((item, index) => (
                  <IndexTable.Row
                    id={item.variant_id.toString()}
                    key={item.variant_id}
                    selected={selectedResources.includes(item.variant_id.toString())}
                    position={index}
                  >
                    <IndexTable.Cell>
                      <Text variant="bodyMd" fontWeight="medium" as="span">
                        {item.variant_name || item.sku}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm" tone="subdued" as="span">
                        {item.sku}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Badge tone={item.bom_level === 0 ? 'success' : 'info'}>
                        {item.bom_level === 0 ? 'Product' : 'Material'}
                      </Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodyMd" as="span">
                        {(item.current_stock || 0).toLocaleString()}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {getStatusBadge(item.stock_status)}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <InlineStack gap="100">
                        <Text variant="bodyMd" as="span">
                          {item.days_of_inventory || 0}
                        </Text>
                        {item.demand_forecast && (
                          <Tooltip content={`Forecast confidence: ${item.demand_forecast.confidence_level}`}>
                            <Badge size="small">
                              AI
                            </Badge>
                          </Tooltip>
                        )}
                      </InlineStack>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <InlineStack gap="100" align="end">
                        <Text variant="bodyMd" fontWeight="medium" as="span">
                          {(item.suggested_order_quantity || 0).toLocaleString()}
                        </Text>
                        {item.suggested_order_quantity && item.suggested_order_quantity > 0 && (
                          <Tooltip content="Create purchase order">
                            <Button
                              size="micro"
                              variant="secondary"
                              icon={PlusIcon}
                              onClick={() => {
                                // TODO: Implement purchase order creation
                                console.log('Create PO for', item.sku);
                              }}
                            />
                          </Tooltip>
                        )}
                      </InlineStack>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodyMd" as="span">
                        {formatCurrency(item.value_in_stock || 0)}
                      </Text>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            )}
            
            {totalPages > 1 && (
              <Box padding="400">
                <InlineStack align="center" gap="200">
                  <Text as="p" variant="bodySm" tone="subdued">
                    Showing {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, filteredAndSortedData.length)} of {filteredAndSortedData.length} items
                  </Text>
                  <Pagination
                    hasPrevious={currentPage > 1}
                    hasNext={currentPage < totalPages}
                    onPrevious={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    onNext={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    label={`Page ${currentPage} of ${totalPages}`}
                  />
                </InlineStack>
              </Box>
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default SimpleInventoryAnalytics;